import type { MerchantPatternDTO } from '@eriko/shared-types';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { Plus, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useRules } from '../../../hooks/useRules';

const CATEGORY_SLUGS: { slug: string; name: string }[] = [
  { slug: 'revenue', name: 'Revenue' },
  { slug: 'materials', name: 'Materials' },
  { slug: 'supplies', name: 'Supplies' },
  { slug: 'office', name: 'Office Expense' },
  { slug: 'vehicle', name: 'Vehicle & Fuel' },
  { slug: 'utilities', name: 'Utilities' },
  { slug: 'software', name: 'Software & Subscriptions' },
  { slug: 'meals', name: 'Meals & Entertainment' },
  { slug: 'transfer', name: 'Transfer' },
  { slug: 'insurance', name: 'Insurance' },
  { slug: 'equipment', name: 'Equipment' },
  { slug: 'payroll', name: 'Payroll' },
  { slug: 'rent', name: 'Rent' },
  { slug: 'travel', name: 'Travel' },
  { slug: 'advertising', name: 'Advertising' },
  { slug: 'professional-services', name: 'Professional Services' },
  { slug: 'other', name: 'Other' },
];

export function RulesTab() {
  const { rules, isLoading, actions } = useRules();
  const [pattern, setPattern] = useState('');
  const [categorySlug, setCategorySlug] = useState<string>('revenue');

  const handleAdd = () => {
    const v = pattern.trim();
    if (!v) return;
    actions.create.mutate(
      { pattern: v, categorySlug },
      { onSuccess: () => setPattern('') },
    );
  };

  const handleDelete = (rule: MerchantPatternDTO) => {
    if (!confirm(`Delete rule "${rule.pattern}"?`)) return;
    actions.remove.mutate(rule.id);
  };

  const categoryName = (slug: string) =>
    CATEGORY_SLUGS.find((c) => c.slug === slug)?.name ?? slug;

  return (
    <div className="max-w-3xl">
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-surface-900 tracking-tight">
          Merchant category rules
        </h2>
        <p className="text-sm text-surface-500 mt-1.5 leading-relaxed">
          Auto-categorize transactions by merchant name pattern. Rules are applied before Eriko
          guesses a category.
        </p>
      </header>

      <div className="mb-6 rounded-xl border border-surface-200 bg-surface-0 p-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
            placeholder="e.g. AMAZON, Starbucks, AWS*"
            maxLength={200}
            className="h-10 flex-1 rounded-lg border border-surface-300 bg-surface-0 px-3.5 text-sm text-surface-900 placeholder:text-surface-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:opacity-60"
            disabled={actions.create.isPending}
          />
          <Select
            value={categorySlug}
            onValueChange={setCategorySlug}
            disabled={actions.create.isPending}
          >
            <SelectTrigger className="h-10 sm:w-56 rounded-lg border-surface-300 bg-surface-0 text-sm font-medium text-surface-800 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_SLUGS.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="lg"
            onClick={handleAdd}
            disabled={!pattern.trim() || actions.create.isPending}
            className='h-10'
          >
            <Plus className="h-4 w-4" strokeWidth={2.4} />
            {actions.create.isPending ? 'Adding…' : 'Add rule'}
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-surface-500">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-surface-400" />
          Loading rules…
        </div>
      )}

      {!isLoading && rules.length === 0 && (
        <div className="rounded-xl border border-dashed border-surface-300 bg-surface-50/60 px-6 py-12 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-100 text-surface-500">
            <Tag className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <p className="text-sm font-semibold text-surface-800">No rules yet</p>
          <p className="mx-auto mt-1 max-w-sm text-xs text-surface-500 leading-relaxed">
            Add a pattern above and Eriko will apply it automatically when categorizing
            transactions.
          </p>
        </div>
      )}

      {!isLoading && rules.length > 0 && (
        <ul className="divide-y divide-surface-200 overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-sm">
          {rules.map((rule: MerchantPatternDTO) => (
            <li
              key={rule.id}
              className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-50"
            >
              <div className="flex-1 min-w-0">
                <p className="truncate font-mono text-sm font-medium text-surface-900">
                  {rule.pattern}
                </p>
                <div className="mt-1 flex items-center gap-2 text-xs text-surface-500">
                  <span className="inline-flex items-center rounded-full bg-surface-100 px-2 py-0.5 text-[11px] font-medium text-surface-800 border border-surface-200">
                    {categoryName(rule.categorySlug)}
                  </span>
                  {rule.usageCount > 1 && (
                    <span className="text-surface-400">
                      applied {rule.usageCount} times
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 text-surface-500 hover:bg-neg/10 hover:text-neg"
                onClick={() => handleDelete(rule)}
                disabled={actions.remove.isPending}
                title="Delete rule"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.8} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
